export const DEMO_SESSION_MINUTES = 30;

export const DEMO_FIELD_LIMITS = Object.freeze({
  name: 80,
  organisationName: 120,
  role: 80,
  location: 100,
  whatsapp: 20,
  email: 160,
  title: 80,
  message: 280,
});

export const DEMO_KULIAH_LIMITS = Object.freeze({
  title: 80,
  speakerId: 80,
  speakerName: 100,
  speakerImage: 200,
  kitabId: 80,
  kitabTitle: 120,
  topic: 160,
});

export const DEMO_ETV_MODES = Object.freeze([
  "normal",
  "sequence",
  "countdown",
  "iqamah",
  "dark",
]);

export const DEMO_SEQUENCE_SECONDS = Object.freeze({ countdown: 15, iqamah: 10, dark: 8 });

const trimText = (value) => String(value ?? "").trim();

const normalizeWhatsapp = (value) => {
  let normalized = trimText(value).replace(/[\s()-]/g, "");

  if (normalized.startsWith("00")) normalized = `+${normalized.slice(2)}`;
  else if (normalized.startsWith("0")) normalized = `+60${normalized.slice(1)}`;

  return normalized;
};

export const normalizeLeadInput = (raw = {}) => ({
  name: trimText(raw.name),
  organisationName: trimText(raw.organisationName),
  role: trimText(raw.role),
  location: trimText(raw.location),
  whatsapp: normalizeWhatsapp(raw.whatsapp),
  email: trimText(raw.email).toLowerCase(),
  consentToContact: raw.consentToContact === true,
});

const requiredTextError = (value, label, maxLength) => {
  if (!value) return `${label} diperlukan.`;
  if (value.length > maxLength) return `${label} melebihi ${maxLength} aksara.`;
  return "";
};

export const validateLeadInput = (raw = {}) => {
  const value = normalizeLeadInput(raw);
  const errors = {};

  const requiredFields = [
    ["name", "Nama", DEMO_FIELD_LIMITS.name],
    ["organisationName", "Nama masjid atau surau", DEMO_FIELD_LIMITS.organisationName],
    ["role", "Jawatan atau peranan", DEMO_FIELD_LIMITS.role],
    ["location", "Negeri atau daerah", DEMO_FIELD_LIMITS.location],
    ["whatsapp", "Nombor WhatsApp", DEMO_FIELD_LIMITS.whatsapp],
  ];

  requiredFields.forEach(([field, label, maxLength]) => {
    const error = requiredTextError(value[field], label, maxLength);
    if (error) errors[field] = error;
  });

  if (
    value.whatsapp &&
    value.whatsapp.length <= DEMO_FIELD_LIMITS.whatsapp &&
    !/^\+[1-9]\d{7,14}$/.test(value.whatsapp)
  ) {
    errors.whatsapp = "Masukkan nombor WhatsApp yang sah.";
  }

  if (value.email.length > DEMO_FIELD_LIMITS.email) {
    errors.email = `E-mel melebihi ${DEMO_FIELD_LIMITS.email} aksara.`;
  } else if (value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) {
    errors.email = "Masukkan alamat e-mel yang sah.";
  }

  if (!value.consentToContact) {
    errors.consentToContact = "Persetujuan diperlukan untuk memulakan demo.";
  }

  return { valid: Object.keys(errors).length === 0, errors, value };
};

export const normalizeAnnouncement = (raw = {}) => ({
  title: trimText(raw.title),
  message: trimText(raw.message),
});

export const validateAnnouncement = (raw = {}) => {
  const value = normalizeAnnouncement(raw);
  const errors = {};

  const titleError = requiredTextError(value.title, "Tajuk", DEMO_FIELD_LIMITS.title);
  const messageError = requiredTextError(value.message, "Mesej", DEMO_FIELD_LIMITS.message);
  if (titleError) errors.title = titleError;
  if (messageError) errors.message = messageError;

  return { valid: Object.keys(errors).length === 0, errors, value };
};

const boundedString = (value) => (typeof value === "string" ? value.trim() : "");
const MALAYSIA_OFFSET_MS = 8 * 60 * 60 * 1000;

const normalizeTimestampValue = (value) => {
  if (value instanceof Date && Number.isFinite(value.getTime())) return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && Number.isFinite(Date.parse(value))) return value;
  if (value && typeof value.toMillis === "function") return value;
  return null;
};

export const normalizeDemoKuliah = (raw = {}) => {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    title: boundedString(source.title),
    speakerId: boundedString(source.speakerId),
    speakerName: boundedString(source.speakerName),
    speakerImage: boundedString(source.speakerImage),
    kitabId: boundedString(source.kitabId),
    kitabTitle: boundedString(source.kitabTitle),
    topic: boundedString(source.topic),
    date: boundedString(source.date),
    startTime: boundedString(source.startTime),
    publishedAt: normalizeTimestampValue(source.publishedAt),
  };
};

export const demoKuliahStartMillis = ({ date, startTime }) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) {
    return null;
  }
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = startTime.split(":").map(Number);
  const millis = Date.UTC(year, month - 1, day, hour - 8, minute);
  const malaysiaWallTime = new Date(millis + MALAYSIA_OFFSET_MS);
  if (
    malaysiaWallTime.getUTCFullYear() !== year
    || malaysiaWallTime.getUTCMonth() !== month - 1
    || malaysiaWallTime.getUTCDate() !== day
    || malaysiaWallTime.getUTCHours() !== hour
    || malaysiaWallTime.getUTCMinutes() !== minute
  ) return null;
  return millis;
};

export const formatMalaysiaDate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kuala_Lumpur",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

export const validateDemoKuliah = (raw = {}, { now = new Date() } = {}) => {
  const value = normalizeDemoKuliah(raw);
  const errors = {};
  const requiredFields = [
    ["title", "Tajuk"],
    ["speakerId", "Ustaz"],
    ["speakerName", "Nama ustaz"],
    ["speakerImage", "Imej ustaz"],
    ["kitabId", "Kitab"],
    ["kitabTitle", "Nama kitab"],
    ["topic", "Topik"],
  ];

  requiredFields.forEach(([field, label]) => {
    const error = requiredTextError(value[field], label, DEMO_KULIAH_LIMITS[field]);
    if (error) errors[field] = error;
  });

  const startMillis = demoKuliahStartMillis(value);
  const nowDate = now instanceof Date ? now : new Date(now);
  if (!Number.isFinite(startMillis) || !Number.isFinite(nowDate.getTime())) {
    errors.dateTime = "Masukkan tarikh dan masa yang sah.";
  } else if (startMillis - nowDate.getTime() < 30 * 60 * 1000) {
    errors.dateTime = "Kuliah mesti dijadualkan sekurang-kurangnya 30 minit lagi.";
  }

  return { valid: Object.keys(errors).length === 0, errors, value };
};

export const normalizeEtvPreview = (raw = {}) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const mode = DEMO_ETV_MODES.includes(source.mode) ? source.mode : "normal";
  const prayer = boundedString(source.prayer) || "Maghrib";
  const sequence = Number.isSafeInteger(source.sequence) && source.sequence >= 0
    ? source.sequence
    : 0;
  return {
    mode,
    prayer: prayer.slice(0, 40),
    sequence,
    startedAt: normalizeTimestampValue(source.startedAt),
  };
};

const timestampToMillis = (value) => {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value === "string") return Date.parse(value);
  return Number.NaN;
};

export const deriveDemoStage = (preview = {}, nowMs = Date.now()) => {
  const normalized = normalizeEtvPreview(preview);
  if (normalized.mode !== "sequence") {
    return {
      stage: normalized.mode,
      secondsRemaining: 0,
    };
  }

  const startedAtMs = timestampToMillis(normalized.startedAt);
  if (!Number.isFinite(startedAtMs) || nowMs < startedAtMs) {
    return { stage: "normal", secondsRemaining: 0 };
  }

  const elapsed = nowMs - startedAtMs;
  const countdownMs = DEMO_SEQUENCE_SECONDS.countdown * 1000;
  const iqamahEndMs = countdownMs + DEMO_SEQUENCE_SECONDS.iqamah * 1000;
  const darkEndMs = iqamahEndMs + DEMO_SEQUENCE_SECONDS.dark * 1000;
  if (elapsed < countdownMs) {
    return { stage: "countdown", secondsRemaining: Math.ceil((countdownMs - elapsed) / 1000) };
  }
  if (elapsed < iqamahEndMs) {
    return { stage: "iqamah", secondsRemaining: Math.ceil((iqamahEndMs - elapsed) / 1000) };
  }
  if (elapsed < darkEndMs) {
    return { stage: "dark", secondsRemaining: Math.ceil((darkEndMs - elapsed) / 1000) };
  }
  return { stage: "normal", secondsRemaining: 0 };
};

const expiryToMillis = (expiresAt) => {
  if (typeof expiresAt?.toMillis === "function") return expiresAt.toMillis();
  if (expiresAt instanceof Date) return expiresAt.getTime();
  if (typeof expiresAt === "number") return expiresAt;

  const parsed = Date.parse(expiresAt);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isDemoSessionExpired = (session, nowMs = Date.now()) =>
  !session?.expiresAt || nowMs >= expiryToMillis(session.expiresAt);

export const buildEtvDemoUrl = ({ baseUrl, sessionId }) => {
  const normalizedBase = trimText(baseUrl).replace(/\/+$/, "");
  return `${normalizedBase}/#/demo/${encodeURIComponent(trimText(sessionId))}`;
};
